import React, { useEffect, useState } from 'react';
import { CommonCard } from '@/components/common/CommonCard';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import { Settings, Truck, ShoppingBag, Package } from 'lucide-react';
import { IRole } from '@/interfaces/Role/role.interface';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export interface IPaymentSetting {
    deliveryType: 'courier' | 'takeaway' | 'delivery' | string;
    cardPaymentStatus: 'active' | 'inactive';
    walletPaymentStatus: 'active' | 'inactive';
    codPaymentStatus: 'active' | 'inactive';
}

interface IGlobalSettings {
    id?: string;
    tax: string;
    supportNumber: string;
    fixDeliveryCharge: string;
    fixCourierCharge: string;
    deliveryFeeAfterRadiusPerKm: string;
    platformCharge: string;
    minimumOrderValue: string;
    deliveryRadius: string;
    returnWindowHours: string;
    driverBaseFare: string;
    driverPerKmRate: string;
    maxActiveOrdersPerDriver: string;
    storeDefaultRoleId?: string;
    taxStatus: 'active' | 'inactive';
    paymentSettings?: IPaymentSetting[];
}

interface IGlobalSettingsResponse {
    id?: string;
    tax?: string;
    supportNumber?: string;
    fixDeliveryCharge?: string;
    fixCourierCharge?: string;
    deliveryFeeAfterRadiusPerKm?: string;
    platformCharge?: string;
    minimumOrderValue?: string;
    deliveryRadius?: string;
    returnWindowHours?: string;
    driverBaseFare?: string;
    driverPerKmRate?: string;
    maxActiveOrdersPerDriver?: string;
    storeDefaultRoleId?: string;
    taxStatus?: 'active' | 'inactive';
    paymentSettings?: IPaymentSetting[];
}

const DEFAULT_PAYMENT_SETTINGS: IPaymentSetting[] = [
    {
        deliveryType: 'delivery',
        cardPaymentStatus: 'inactive',
        walletPaymentStatus: 'inactive',
        codPaymentStatus: 'inactive',
    },
    {
        deliveryType: 'courier',
        cardPaymentStatus: 'inactive',
        walletPaymentStatus: 'inactive',
        codPaymentStatus: 'inactive',
    },
    {
        deliveryType: 'takeaway',
        cardPaymentStatus: 'inactive',
        walletPaymentStatus: 'inactive',
        codPaymentStatus: 'inactive',
    },
];

const AdminSettings = () => {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    const [settings, setSettings] = useState<IGlobalSettings>({
        tax: '0',
        supportNumber: '',
        fixDeliveryCharge: '0',
        fixCourierCharge: '0',
        deliveryRadius: '5',
        deliveryFeeAfterRadiusPerKm: '0',
        platformCharge: '0',
        minimumOrderValue: '0',
        returnWindowHours: '0',
        driverBaseFare: '0',
        driverPerKmRate: '0',
        maxActiveOrdersPerDriver: '1',
        storeDefaultRoleId: '',
        taxStatus: 'inactive',
        paymentSettings: DEFAULT_PAYMENT_SETTINGS,
    });

    const [roles, setRoles] = useState<IRole[]>([]);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                setFetching(true);
                const response = await callApi<{ data: IGlobalSettingsResponse | IGlobalSettingsResponse[] }>(
                    `${API_URL}${API_ENDPOINT.settings}`,
                    { method: 'GET' }
                );

                const settingsData = Array.isArray(response?.data) ? response?.data[0] : response?.data;

                if (settingsData) {
                    const rawPaymentSettings = settingsData.paymentSettings || [];
                    const deliveryTypes = ['delivery', 'courier', 'takeaway'];

                    const mergedPaymentSettings = deliveryTypes.map(type => {
                        const existing = Array.isArray(rawPaymentSettings)
                            ? rawPaymentSettings.find((p: IPaymentSetting) => p.deliveryType === type)
                            : undefined;

                        return existing ? {
                            deliveryType: type,
                            cardPaymentStatus: existing.cardPaymentStatus || 'active',
                            walletPaymentStatus: existing.walletPaymentStatus || 'active',
                            codPaymentStatus: existing.codPaymentStatus || 'active',
                        } : {
                            deliveryType: type,
                            cardPaymentStatus: 'inactive' as const,
                            walletPaymentStatus: 'inactive' as const,
                            codPaymentStatus: 'inactive' as const,
                        };
                    });


                    setSettings({
                        id: settingsData.id,
                        tax: settingsData.tax?.toString() || '',
                        supportNumber: settingsData.supportNumber || '',
                        fixDeliveryCharge: settingsData.fixDeliveryCharge?.toString() || '',
                        fixCourierCharge: settingsData.fixCourierCharge?.toString() || '',
                        deliveryRadius: settingsData.deliveryRadius?.toString() || '',
                        deliveryFeeAfterRadiusPerKm: settingsData.deliveryFeeAfterRadiusPerKm?.toString() || '',
                        platformCharge: settingsData.platformCharge?.toString() || '',
                        minimumOrderValue: settingsData.minimumOrderValue?.toString() || '',
                        returnWindowHours: settingsData.returnWindowHours?.toString() || '',
                        driverBaseFare: settingsData.driverBaseFare?.toString() || '',
                        driverPerKmRate: settingsData.driverPerKmRate?.toString() || '',
                        maxActiveOrdersPerDriver: settingsData.maxActiveOrdersPerDriver?.toString() || '1',
                        storeDefaultRoleId: settingsData.storeDefaultRoleId || '',
                        taxStatus: settingsData.taxStatus || 'inactive',
                        paymentSettings: mergedPaymentSettings,
                    });
                }
            } catch (error) {
                console.error("Failed to fetch settings:", error);
            } finally {
                setFetching(false);
            }
        };

        const fetchRoles = async () => {
            try {
                const res = await callApi<any>(
                    `${API_URL}${API_ENDPOINT.roles}`,
                    { method: 'GET' }
                );
                const rolesList = res?.data?.roles || res?.data || res || [];
                setRoles(Array.isArray(rolesList) ? rolesList : []);
            } catch (error) {
                console.error("Failed to fetch roles:", error);
            }
        };

        fetchSettings();
        fetchRoles();
    }, []);

    const handleSave = async () => {
        const paymentList = settings.paymentSettings || DEFAULT_PAYMENT_SETTINGS;
        const invalidSetting = paymentList.find(
            item => item.cardPaymentStatus === 'inactive' &&
                item.codPaymentStatus === 'inactive' &&
                item.walletPaymentStatus === 'inactive'
        );

        if (invalidSetting) {
            const deliveryLabel = invalidSetting.deliveryType === 'takeaway'
                ? 'Collection'
                : invalidSetting.deliveryType === 'delivery'
                    ? 'Home Delivery'
                    : 'Courier Service';
            showToastNotification('danger', `At least one payment method must be active for ${deliveryLabel}.`);
            return;
        }

        setLoading(true);
        try {
            const url = settings.id
                ? `${API_URL}${API_ENDPOINT.settings}?settingId=${settings.id}`
                : `${API_URL}${API_ENDPOINT.settings}`;

            await callApi(
                url,
                {
                    method: 'POST',
                    data: {
                        tax: Number(settings.tax),
                        supportNumber: settings.supportNumber,
                        fixDeliveryCharge: Number(settings.fixDeliveryCharge),
                        fixCourierCharge: Number(settings.fixCourierCharge),
                        deliveryFeeAfterRadiusPerKm: Number(settings.deliveryFeeAfterRadiusPerKm),
                        platformCharge: Number(settings.platformCharge),
                        minimumOrderValue: Number(settings.minimumOrderValue),
                        returnWindowHours: Number(settings.returnWindowHours),
                        driverBaseFare: Number(settings.driverBaseFare),
                        driverPerKmRate: Number(settings.driverPerKmRate),
                        maxActiveOrdersPerDriver: Number(settings.maxActiveOrdersPerDriver || 1),
                        storeDefaultRoleId: settings.storeDefaultRoleId,
                        taxStatus: settings.taxStatus,
                        paymentSettings: settings.paymentSettings || DEFAULT_PAYMENT_SETTINGS,
                    }
                }
            );
            showToastNotification('success', 'Settings updated successfully');
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: keyof IGlobalSettings, value: any) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const handlePaymentSettingChange = (
        deliveryType: string,
        field: keyof IPaymentSetting,
        value: 'active' | 'inactive'
    ) => {
        if (value === 'inactive') {
            const currentList = settings.paymentSettings || DEFAULT_PAYMENT_SETTINGS;
            const currentItem = currentList.find(item => item.deliveryType === deliveryType);

            if (currentItem) {
                const activeCount = [
                    currentItem.cardPaymentStatus,
                    currentItem.codPaymentStatus,
                    currentItem.walletPaymentStatus
                ].filter(status => status === 'active').length;

                if (currentItem[field] === 'active' && activeCount <= 1) {
                    showToastNotification('danger', 'At least one payment method (Card, COD, or Wallet) must remain active for each delivery type.');
                    return;
                }
            }
        }

        setSettings(prev => {
            const currentList = prev.paymentSettings || DEFAULT_PAYMENT_SETTINGS;
            const updatedList = currentList.map(item => {
                if (item.deliveryType === deliveryType) {
                    return { ...item, [field]: value };
                }
                return item;
            });
            return { ...prev, paymentSettings: updatedList };
        });
    };

    if (fetching) {
        return (
            <div className="flex justify-center items-center py-12 bg-card border rounded-xl">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <CommonCard
                title="Global Settings"
                icon={<Settings className="text-primary w-5 h-5" />}
                headerAction={
                    <Button
                        onClick={handleSave}
                        isLoading={loading}
                        className="px-6 h-10 bg-[#901B1B] hover:bg-[#7a1616] text-white rounded-md text-sm transition-colors shadow-sm"
                    >
                        Update Settings
                    </Button>
                }
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 p-2">
                    <FormField
                        label="VAT Tax (%)"
                        name="tax"
                        type="number"
                        value={settings.tax}
                        disabled={settings.taxStatus === 'inactive'}
                        onChange={(e) => handleChange('tax', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="0"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <div className="flex flex-col space-y-2 justify-center">
                        <span className="text-sm font-semibold text-foreground">Tax Status</span>
                        <div className="flex items-center space-x-3 h-10">
                            <Switch
                                id="taxStatus"
                                checked={settings.taxStatus === 'active'}
                                onCheckedChange={(checked) => handleChange('taxStatus', checked == true ? "active" : "inactive")}
                            />
                            <Label htmlFor="taxStatus" className="text-sm font-medium cursor-pointer text-muted-foreground">
                                Apply Tax on Orders: <span className={settings.taxStatus === 'active' ? 'text-green-600 font-semibold' : 'text-slate-500 font-semibold'}>{settings.taxStatus === 'active' ? 'Applicable' : 'Not Applicable'}</span>
                            </Label>
                        </div>
                    </div>

                    <FormField
                        label="Support Number"
                        name="supportNumber"
                        type="text"
                        value={settings.supportNumber}
                        onChange={(e) => handleChange('supportNumber', e.target.value)}
                        placeholder="e.g. 3387464849"
                        autoComplete="off"
                        inputClass="w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Store Delivery Charge"
                        name="fixDeliveryCharge"
                        type="number"
                        value={settings.fixDeliveryCharge}
                        onChange={(e) => handleChange('fixDeliveryCharge', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="0.00"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Store Courier Charge"
                        name="fixCourierCharge"
                        type="number"
                        value={settings.fixCourierCharge}
                        onChange={(e) => handleChange('fixCourierCharge', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="0.00"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Delivery Fee After Radius (Per Km)"
                        name="deliveryFeeAfterRadiusPerKm"
                        type="number"
                        value={settings.deliveryFeeAfterRadiusPerKm}
                        onChange={(e) => handleChange('deliveryFeeAfterRadiusPerKm', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="0.00"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Platform Charge"
                        name="platformCharge"
                        type="number"
                        value={settings.platformCharge}
                        onChange={(e) => handleChange('platformCharge', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="0.00"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Minimum Order Value"
                        name="minimumOrderValue"
                        type="number"
                        value={settings.minimumOrderValue}
                        onChange={(e) => handleChange('minimumOrderValue', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="0.00"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Return Window (Hours)"
                        name="returnWindowHours"
                        type="number"
                        value={settings.returnWindowHours}
                        onChange={(e) => handleChange('returnWindowHours', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="e.g. 24 or 48"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Driver Base Fare"
                        name="driverBaseFare"
                        type="number"
                        value={settings.driverBaseFare}
                        onChange={(e) => handleChange('driverBaseFare', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="e.g. 2"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Driver Per Km Rate"
                        name="driverPerKmRate"
                        type="number"
                        value={settings.driverPerKmRate}
                        onChange={(e) => handleChange('driverPerKmRate', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="e.g. 2"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Max Active Orders Per Driver"
                        name="maxActiveOrdersPerDriver"
                        type="number"
                        value={settings.maxActiveOrdersPerDriver}
                        onChange={(e) => handleChange('maxActiveOrdersPerDriver', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="1"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <div className="flex flex-col">
                        <FormField
                            label="Store Default Role"
                            name="storeDefaultRoleId"
                            type="select"
                            value={settings.storeDefaultRoleId || ''}
                            onValueChange={(val) => handleChange('storeDefaultRoleId', val)}
                            options={roles.map(r => ({ label: r.name, value: r.id }))}
                        />
                    </div>

                    {/* Payment Method Status Controls by Delivery Type */}
                    <div className="md:col-span-2 pt-6 border-t space-y-6">
                        <div>
                            <h4 className="text-sm font-bold text-foreground opacity-90 uppercase tracking-wider">
                                Payment Method Settings by Delivery Type
                            </h4>
                            <p className="text-xs text-muted-foreground mt-1">
                                Configure payment options individually for Delivery, Courier, and Collection orders.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {(settings.paymentSettings || DEFAULT_PAYMENT_SETTINGS).map((paySetting) => {
                                const getDeliveryTitle = (type: string) => {
                                    switch (type) {
                                        case 'delivery': return 'Home Delivery';
                                        case 'courier': return 'Courier Service';
                                        case 'takeaway': return 'Collection';
                                        default: return type.toUpperCase();
                                    }
                                };

                                const getDeliveryIcon = (type: string) => {
                                    switch (type) {
                                        case 'delivery': return <Truck className="w-4 h-4 text-blue-500" />;
                                        case 'courier': return <Package className="w-4 h-4 text-purple-500" />;
                                        case 'takeaway': return <ShoppingBag className="w-4 h-4 text-amber-500" />;
                                        default: return <Truck className="w-4 h-4 text-primary" />;
                                    }
                                };

                                const getDeliveryBadgeClass = (type: string) => {
                                    switch (type) {
                                        case 'delivery': return 'bg-blue-500/10 text-blue-600 border-blue-200';
                                        case 'courier': return 'bg-purple-500/10 text-purple-600 border-purple-200';
                                        case 'takeaway': return 'bg-amber-500/10 text-amber-600 border-amber-200';
                                        default: return 'bg-slate-500/10 text-slate-600 border-slate-200';
                                    }
                                };

                                return (
                                    <div key={paySetting.deliveryType} className="border border-border/80 rounded-2xl p-5 bg-card/60 shadow-sm space-y-4">
                                        <div className="flex items-center justify-between pb-3 border-b">
                                            <h5 className="font-bold text-sm text-foreground flex items-center gap-2">
                                                {getDeliveryIcon(paySetting.deliveryType)}
                                                {getDeliveryTitle(paySetting.deliveryType)}
                                            </h5>
                                            <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border capitalize ${getDeliveryBadgeClass(paySetting.deliveryType)}`}>
                                                {paySetting.deliveryType === 'takeaway' ? 'collection' : paySetting.deliveryType}
                                            </span>
                                        </div>

                                        <div className="space-y-3">
                                            {/* Card Payment */}
                                            <div className="flex items-center justify-between p-3 rounded-xl border border-border/60 bg-secondary/5">
                                                <div className="space-y-0.5">
                                                    <Label className="text-xs font-semibold cursor-pointer">
                                                        Card Payment
                                                    </Label>
                                                    <p className="text-[10px] text-muted-foreground">
                                                        Status: <span className={paySetting.cardPaymentStatus === 'active' ? 'text-green-600 font-bold' : 'text-slate-500 font-bold'}>
                                                            {paySetting.cardPaymentStatus === 'active' ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </p>
                                                </div>
                                                <Switch
                                                    checked={paySetting.cardPaymentStatus === 'active'}
                                                    onCheckedChange={(checked) => handlePaymentSettingChange(paySetting.deliveryType, 'cardPaymentStatus', checked ? 'active' : 'inactive')}
                                                />
                                            </div>

                                            {/* COD Payment */}
                                            <div className="flex items-center justify-between p-3 rounded-xl border border-border/60 bg-secondary/5">
                                                <div className="space-y-0.5">
                                                    <Label className="text-xs font-semibold cursor-pointer">
                                                        Cash on Delivery (COD)
                                                    </Label>
                                                    <p className="text-[10px] text-muted-foreground">
                                                        Status: <span className={paySetting.codPaymentStatus === 'active' ? 'text-green-600 font-bold' : 'text-slate-500 font-bold'}>
                                                            {paySetting.codPaymentStatus === 'active' ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </p>
                                                </div>
                                                <Switch
                                                    checked={paySetting.codPaymentStatus === 'active'}
                                                    onCheckedChange={(checked) => handlePaymentSettingChange(paySetting.deliveryType, 'codPaymentStatus', checked ? 'active' : 'inactive')}
                                                />
                                            </div>

                                            {/* Wallet Payment */}
                                            <div className="flex items-center justify-between p-3 rounded-xl border border-border/60 bg-secondary/5">
                                                <div className="space-y-0.5">
                                                    <Label className="text-xs font-semibold cursor-pointer">
                                                        Wallet Payment
                                                    </Label>
                                                    <p className="text-[10px] text-muted-foreground">
                                                        Status: <span className={paySetting.walletPaymentStatus === 'active' ? 'text-green-600 font-bold' : 'text-slate-500 font-bold'}>
                                                            {paySetting.walletPaymentStatus === 'active' ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </p>
                                                </div>
                                                <Switch
                                                    checked={paySetting.walletPaymentStatus === 'active'}
                                                    onCheckedChange={(checked) => handlePaymentSettingChange(paySetting.deliveryType, 'walletPaymentStatus', checked ? 'active' : 'inactive')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </CommonCard>
        </div>
    );
};

export default AdminSettings;
